/**
 * What the SELLER has actually done in the portal, as opposed to what merely
 * exists in the store.
 *
 * The setup helper reads real store state, which is right for "is this store
 * configured" but wrong for "does this seller know how to run it". The quick
 * setup wizard creates categories on the seller's behalf, so the checklist
 * ticked "add categories" for someone who had never seen the Categories page
 * and still could not add one. Those steps are held open until the seller does
 * the thing themselves, once, from the portal.
 *
 * Per store, and local: this is a teaching aid, not a business record.
 */
const wizardKey = (storeSlug: string) => `a2z:setup-by-wizard:v1:${storeSlug}`;
const portalKey = (storeSlug: string) => `a2z:setup-by-seller:v1:${storeSlug}`;
/**
 * Which steps the seller has already been congratulated for. Shared, not
 * private to the launcher: the celebration for a first product fires from the
 * create flow (which knows the seller just did it, and can show the full
 * animation), while the launcher congratulates steps completed anywhere else.
 * One ledger means the seller is never congratulated twice for one step.
 */
const celebratedKey = (storeSlug: string) => `a2z:setup-celebrated:v1:${storeSlug}`;

/** Steps that can be satisfied by the wizard and therefore need re-doing. */
export type TeachableStep = "categories" | "products";

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
}

function add(key: string, id: string): void {
  try {
    const next = new Set(read(key));
    next.add(id);
    localStorage.setItem(key, JSON.stringify([...next]));
  } catch {
    /* private browsing — the helper just shows one extra step */
  }
}

/** Called by the wizard for each step it completed on the seller's behalf. */
export function markDoneByWizard(storeSlug: string, id: TeachableStep): void {
  add(wizardKey(storeSlug), id);
}

/** Called when the seller creates one themselves, from the portal. */
export function markDoneBySeller(storeSlug: string, id: TeachableStep): void {
  add(portalKey(storeSlug), id);
}

/**
 * True when this step exists only because the wizard made it — the store has
 * the data, but the seller has not been shown where it comes from.
 */
export function needsSellerToDoIt(storeSlug: string, id: string): boolean {
  if (typeof window === "undefined") return false;
  if (id !== "categories" && id !== "products") return false;
  return read(wizardKey(storeSlug)).includes(id) &&
    !read(portalKey(storeSlug)).includes(id);
}

/** Steps the seller has already been congratulated for, in this browser. */
export function readCelebrated(storeSlug: string): string[] {
  if (typeof window === "undefined") return [];
  return read(celebratedKey(storeSlug));
}

/** Records a congratulation so no other surface repeats it. */
export function markCelebrated(storeSlug: string, ids: readonly string[]): void {
  try {
    const next = new Set(read(celebratedKey(storeSlug)));
    for (const id of ids) next.add(id);
    localStorage.setItem(celebratedKey(storeSlug), JSON.stringify([...next]));
  } catch {
    /* private browsing — worst case the seller is congratulated twice */
  }
}
